/**
 * Mapeamento entre rotas da API e rotas do frontend
 * 
 * Este arquivo centraliza a conversão entre os padrões de rota da API
 * (ex: 'taxes.*') e as rotas do Vue Router (ex: '/taxes')
 * 
 * Formato:
 * - Chave: padrão de rota da API (suporta wildcards com *)
 * - Valor: path da rota do frontend (null se não for uma página explícita)
 */

export const API_TO_FRONTEND_ROUTES = {
    // Rotas principais
    'schedule.*': '/',
    'dashboard.*': '/dashboard',
    'customers.*': '/customers',
    'sessions.*': '/sessions',
    'contract-templates.*': '/contract-templates',
    
    // Rotas Pro (geralmente bloqueadas para Starter)
    'taxes.*': '/taxes',
    'vouchers.*': '/vouchers',
    'notifications.*': '/notifications',
    'reschedule-requests.*': '/reschedule-requests',
    
    // Rotas de API que não têm página explícita no frontend
    'contract.*': null,
    'emergency-contact.*': null,
    'extracts.*': null,
    'user': null,
    'user.*': null,
    'professionals.*': null,
    'service.getServicesByCustomer': null,
    'google.*': null,
    'whatsapp.*': null,
    'taxes_categories.*': null,
    'extract.without-voucher': null
};

/**
 * Converte uma lista de rotas da API para rotas do frontend
 * @param {string[]} apiRoutes - Array de rotas da API
 * @returns {string[]} Array de rotas do frontend (sem nulls)
 */
export function convertApiRoutesToFrontend(apiRoutes) {
    if (!apiRoutes || !Array.isArray(apiRoutes)) return [];
    
    const frontendRoutes = new Set();
    
    apiRoutes.forEach(apiRoute => {
        // Procurar correspondência exata primeiro
        if (API_TO_FRONTEND_ROUTES[apiRoute]) {
            frontendRoutes.add(API_TO_FRONTEND_ROUTES[apiRoute]);
            return;
        }
        
        // Procurar correspondência com wildcard
        for (const [pattern, frontendPath] of Object.entries(API_TO_FRONTEND_ROUTES)) {
            if (pattern.includes('*')) {
                // Converter padrão para regex
                const regexPattern = '^' + pattern.replace(/\*/g, '.*').replace(/\./g, '\\.');
                const regex = new RegExp(regexPattern);
                
                if (regex.test(apiRoute) && frontendPath !== null) {
                    frontendRoutes.add(frontendPath);
                    break;
                }
            }
        }
    });
    
    // Remover nulls e retornar array
    return Array.from(frontendRoutes).filter(route => route !== null);
}

/**
 * Verifica se uma rota do frontend está bloqueada
 * @param {string} frontendPath - Path da rota do frontend (ex: '/taxes')
 * @param {string[]} blockedApiRoutes - Array de rotas bloqueadas da API
 * @returns {boolean} true se a rota está bloqueada
 */
export function isFrontendRouteBlocked(frontendPath, blockedApiRoutes) {
    if (!blockedApiRoutes || !Array.isArray(blockedApiRoutes)) return false;
    
    const blockedFrontendRoutes = convertApiRoutesToFrontend(blockedApiRoutes);
    
    // Normalizar path (remover trailing slash)
    const normalizedPath = frontendPath.replace(/\/$/, '') || '/';
    
    return blockedFrontendRoutes.some(blockedPath => {
        const normalizedBlockedPath = blockedPath.replace(/\/$/, '') || '/';
        return normalizedPath === normalizedBlockedPath;
    });
}

/**
 * Verifica se uma rota do frontend está permitida
 * @param {string} frontendPath - Path da rota do frontend (ex: '/customers')
 * @param {string[]} allowedApiRoutes - Array de rotas permitidas da API
 * @returns {boolean} true se a rota está permitida
 */
export function isFrontendRouteAllowed(frontendPath, allowedApiRoutes) {
    if (!allowedApiRoutes || !Array.isArray(allowedApiRoutes)) return false;
    
    const allowedFrontendRoutes = convertApiRoutesToFrontend(allowedApiRoutes);
    
    // Normalizar path
    const normalizedPath = frontendPath.replace(/\/$/, '') || '/';
    
    return allowedFrontendRoutes.some(allowedPath => {
        const normalizedAllowedPath = allowedPath.replace(/\/$/, '') || '/';
        return normalizedPath === normalizedAllowedPath;
    });
}
